import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Organization, Person } from 'types';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import { useIsSuperAdminNonImpersonating } from 'utils/models/User';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { ORGANIZATION_SETTINGS_USER_LISTS } from '../../utils/models/Organization';
import { loadOrRender } from '../../utils/util/formatter';
import CardHeaderTitle from '../Widgets/Cards/CardHeaderTitle';
import { AdvancedCurrentPeopleFilterContextProvider } from '../Widgets/Filters/AdvancedSpecializedFilter';
import Loading from '../Widgets/Loading';
import FeatureAudienceEditModal from './FeatureAdministration/FeatureAudienceEditModal';
import FeatureAudienceTable from './FeatureAdministration/FeatureAudienceTable';
import { FeatureAudienceFlag } from './FeatureAdministration/types';
import { useFeatureAudiences } from './FeatureAdministration/useFeatureAudiences';

const stripUserListKeys = (dict) => {
  const d = {};

  Object.keys(dict).forEach((k) => {
    if (ORGANIZATION_SETTINGS_USER_LISTS.indexOf(k) === -1) {
      d[k] = dict[k];
    }
  });

  return d;
};

interface Props {
  currentOrganization: Organization;
  currentProxyPerson?: Person;
}

const FeatureAdministration: FC<Props> = (props) => {
  const [organizationDetails, setOrganizationDetails] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(null);

  const { user } = useAuthProvider();
  const userSub = user?.sub;
  const isSuperAdmin = useIsSuperAdminNonImpersonating();

  const audiences = useFeatureAudiences();
  const [editingFlag, setEditingFlag] = useState<FeatureAudienceFlag | null>(
    null
  );
  const [isEditOpen, setIsEditOpen] = useState(false);
  const openEdit = useCallback((flag: FeatureAudienceFlag) => {
    setEditingFlag(flag);
    setIsEditOpen(true);
  }, []);

  // flags that list the one being edited among their dependencies
  const dependents = useMemo(() => {
    if (!editingFlag || !audiences.sections) return [];
    return audiences.sections
      .flatMap((section) => [
        ...(section.header ? [section.header] : []),
        ...section.flags,
      ])
      .filter((f) => f.depends_on.some((d) => d.name === editingFlag.name))
      .map((f) => ({ name: f.name, display_name: f.display_name }));
  }, [editingFlag, audiences.sections]);

  // the raw settings dump is for superusers only, so is its request
  useEffect(() => {
    if (isSuperAdmin && props.currentOrganization?.id) {
      const url =
        '/organizations/' + props.currentOrganization.id + '/features';
      ConfirmAPI.getUrlWithCache(
        url,
        url,
        userSub,
        props.currentProxyPerson,
        {},
        (data) => {
          setOrganizationDetails(data);
        },
        (message) => {
          setErrorMessage(message);
        }
      );
    }
  }, [
    isSuperAdmin,
    props.currentOrganization?.id,
    userSub,
    props.currentProxyPerson,
  ]);

  const loadOrRenderOutput = isSuperAdmin
    ? loadOrRender(organizationDetails, errorMessage)
    : null;
  if (loadOrRenderOutput) {
    return loadOrRenderOutput;
  }

  return (
    <>
      <p className="text-muted mb-4">
        <FormattedMessage
          id="app.views.organization.feature_administration.intro"
          defaultMessage="
        Switch a feature on or off for everyone in your organization, or set
        an exception for people matching a filter.
      "
        />
      </p>
      {audiences.status === 'ERROR' && (
        <p className="text-danger">
          <FormattedMessage
            id="app.views.organization.feature_administration.load_error"
            defaultMessage="The features could not be loaded. Try again later."
          />
        </p>
      )}
      {audiences.status === 'LOADING' && !audiences.sections && <Loading />}
      {audiences.sections && (
        <AdvancedCurrentPeopleFilterContextProvider roleScope="SYSTEM_ADMIN">
          <FeatureAudienceTable
            sections={audiences.sections}
            onEdit={openEdit}
          />
          <FeatureAudienceEditModal
            flag={editingFlag}
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            onClosed={() => setEditingFlag(null)}
            saveFlag={audiences.saveFlag}
            dependents={dependents}
          />
        </AdvancedCurrentPeopleFilterContextProvider>
      )}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardHeaderTitle>
              <FormattedMessage
                id="app.views.organization.general_administration.feature_customizations"
                defaultMessage="Feature customizations"
              />
            </CardHeaderTitle>
          </CardHeader>
          <CardBody>
            <p className="text-muted">
              <FormattedMessage
                id="app.views.organization.general_administration.deeper_feature_customization"
                defaultMessage="
            Deeper feature customization for advanced users only. If you have
            any questions, contact customer support.
          "
              />
            </p>
            <div>
              <pre style={{ whiteSpace: 'pre-wrap' }}>
                {/* @ts-expect-error */}
                {organizationDetails?.settings
                  ? JSON.stringify(
                      // @ts-expect-error
                      stripUserListKeys(organizationDetails?.settings),
                      null,
                      2
                    )
                  : ''}
              </pre>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentOrganization, currentProxyPerson } = state;

  return {
    currentOrganization,
    currentProxyPerson,
  };
};

export default connect(mapStateToProps)(React.memo(FeatureAdministration));
